import React, { useRef, useMemo, useCallback, memo, useState, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";

export const Calendario = memo(({ appointments, onDateClick, onEventClick, onMonthChange }) => {
  const calendarRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 250);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Quando mudar de mês/visão no calendário
  const handleDatesSet = useCallback(
    (dateInfo) => {
      const midTime = (dateInfo.start.getTime() + dateInfo.end.getTime()) / 2;
      const midDate = new Date(midTime);
      onMonthChange?.(midDate);
    },
    [onMonthChange]
  );

  // Converte appointments (objeto) em eventos do FullCalendar
  const events = useMemo(() => {
    return Object.entries(appointments || {}).map(([id, appointment]) => ({
      id,
      title: appointment.title,
      start: appointment.initial_time,
      end: appointment.final_time,
      description: appointment.description,
      services: appointment.services,
      customer: appointment.customer,
      user: appointment.user,
      linearGradient: appointment.linearGradient,
      groupIcon: appointment.groupIcon,
      full_appointment: appointment,
    }));
  }, [appointments]);

  const eventContent = useCallback((eventInfo) => {
    const isMobile = windowWidth <= 1024;
    const { groupIcon } = eventInfo.event.extendedProps;

    if (isMobile) {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div className="fc-event-time">{eventInfo.timeText}</div>
          {groupIcon && <span className="group-icon" dangerouslySetInnerHTML={{ __html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="white"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>` }}></span>}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <div className="fc-event-time">{eventInfo.timeText}</div>
        <div className="fc-event-title" style={{ flexGrow: 1, textAlign: 'center' }}>{eventInfo.event.title}</div>
        {groupIcon && <span className="group-icon" dangerouslySetInnerHTML={{
          __html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="white">
    <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
</svg>` }}></span>}
      </div>
    );
  }, [windowWidth]);

  // Aplica cor/gradiente em cada evento
  const eventDidMount = useCallback((info) => {
    if (info.event.extendedProps.linearGradient) {
      info.el.style.background = info.event.extendedProps.linearGradient;
      info.el.style.color = "black";
    }
  }, []);

  // Configuração do calendário
  const calendarOptions = useMemo(
    () => ({
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: "dayGridMonth",
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay",
      },
      slotEventOverlap: false,
      dateClick: onDateClick,
      eventClick: onEventClick,
      datesSet: handleDatesSet,
      slotDuration: "00:30:00",
      slotLabelInterval: "01:00",
      allDaySlot: false,
      nowIndicator: true,
      height: "100%",
      contentHeight: "100%",
      eventTimeFormat: {
        hour: "numeric",
        minute: "2-digit",
        meridiem: "short",
      },
      eventDisplay: "block",
      locale: ptBrLocale,
      eventContent,
      eventDidMount,
      events,
      lazyFetching: true,
      rerenderDelay: 50,
    }),
    [onDateClick, onEventClick, handleDatesSet, eventDidMount, events, eventContent]
  );

  return (
    <div id="main_calendar" style={{ height: "100%" }}>
      <FullCalendar id="userCalendar" ref={calendarRef} {...calendarOptions} />
    </div>
  );
});